import React from "react";
import "./MarkdownRenderer.css"; // styling overrides to remove stray bars
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
// Import the light theme to modify
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Create a clean custom theme based on defaults but without line highlighting
const cleanTheme = {
  'code[class*="language-"]': {
    color: '#333',
    background: 'white',
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    textAlign: 'left',
    whiteSpace: 'pre',
    wordSpacing: 'normal',
    wordBreak: 'normal',
    wordWrap: 'normal',
    lineHeight: '1.5',
    tabSize: 4,
    hyphens: 'none'
  },
  'pre[class*="language-"]': {
    color: '#333',
    background: 'white',
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    textAlign: 'left',
    whiteSpace: 'pre',
    wordSpacing: 'normal',
    wordBreak: 'normal',
    wordWrap: 'normal',
    lineHeight: '1.5',
    tabSize: 4,
    hyphens: 'none',
    margin: 0,
    padding: 0,
    overflow: 'auto',
    border: 'none',
    boxShadow: 'none'
  },
  // Disable line highlighting
  '.react-syntax-highlighter-line-number': {
    background: 'transparent',
    color: '#333'
  },
  '.token-line': {
    background: 'transparent !important'
  },
  // Enhanced syntax highlighting - more vibrant colors
  'comment':      { color: '#008000', fontStyle: 'italic' },    // Darker green
  'string':       { color: '#A31515' },                         // Darker red
  'number':       { color: '#098658' },                         // Deeper green
  'keyword':      { color: '#0000FF', fontWeight: 'bold' },     // Brighter blue
  'function':     { color: '#795E26' },                         // Stronger gold
  'class-name':   { color: '#267f99' },                         // Deeper teal
  'punctuation':  { color: '#000000' },                         // Black instead of light gray
  'operator':     { color: '#000000' }                          // Black instead of light gray
};

const MarkdownRenderer = ({ children, components = {} }) => {
  const [copiedStates, setCopiedStates] = React.useState({});
  const codeBlockIds = React.useRef(new Map());

  const getBlockId = (codeContent) => {
    if (!codeBlockIds.current.has(codeContent)) {
      codeBlockIds.current.set(codeContent, `block_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`);
    }
    return codeBlockIds.current.get(codeContent);
  };

  const handleCopy = (code, blockId) => {
    navigator.clipboard.writeText(code);
    setCopiedStates(prev => ({ ...prev, [blockId]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [blockId]: false }));
    }, 2000);
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      components={{
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          const codeContent = String(children).replace(/\n$/, "");
          // Get a stable block ID for this specific code content
          const blockId = getBlockId(codeContent);

          return !inline && match ? (
            <div className="mq-code-block" style={{ 
                margin: "1rem 0", 
                borderRadius: "8px", 
                overflow: "hidden", 
                border: "1px solid #e1e5e9",
                backgroundColor: "#ffffff",
                background: "#ffffff" 
              }}>
              <div className="mq-code-header" style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                padding: "8px 16px", 
                backgroundColor: "#c9cacbff", 
                fontSize: "12px", 
                color: "#586069" 
              }}>
                <span style={{ fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px", color: "#0366d6" }}>
                  {match[1]}
                </span>
                <button 
                  onClick={() => handleCopy(codeContent, blockId)}
                  style={{
                    background: copiedStates[blockId] ? "#28a745" : "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    color: copiedStates[blockId] ? "#ffffff" : "#586069",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  {copiedStates[blockId] ? "Copied!" : "Copy"}
                </button>
              </div>
              <div style={{height: 1, background: '#e1e5e9', width: '100%'}} />
              <SyntaxHighlighter
                style={{
                  ...cleanTheme,
                  'div[class*="language-"]': {
                    ...(cleanTheme['div[class*="language-"]'] || {}),
                    background: '#ffffff',
                    backgroundColor: '#ffffff',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    boxShadow: 'none'
                  },
                  '.react-syntax-highlighter-line-number': {
                    display: 'none'
                  },
                  // Target cursor elements and hide them
                  '.prism-code::before, .prism-code::after': {
                    display: 'none !important',
                    opacity: 0,
                    visibility: 'hidden'
                  },
                  '.token-line::before, .token-line::after': {
                    display: 'none !important',
                    opacity: 0,
                    visibility: 'hidden'
                  }
                }}
                language={match[1]}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  borderRadius: "0 0 8px 8px",
                  fontSize: "15px",
                  lineHeight: "1.5",
                  padding: "16px",
                  backgroundColor: "#ffffff",
                  background: "#ffffff",
                  boxShadow: "none",
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                  borderLeft: "none",
                  borderRight: "none"
                }}
                codeTagProps={{
                  style: {
                    backgroundColor: "transparent",
                    boxShadow: "none"
                  }
                }}
                wrapLines={true}
                showLineNumbers={false}
                lineProps={() => ({
                  style: {
                    display: "block",
                    background: "transparent",
                    backgroundColor: "transparent", 
                    padding: 0,
                    margin: 0,
                    borderLeft: "none",
                    borderRight: "none", 
                    borderTop: "none",
                    borderBottom: "none",
                    border: "none",
                    boxShadow: "none",
                    position: "relative",
                    outline: "none"
                  }
                })}
                useInlineStyles={true}
              >
                {codeContent}
              </SyntaxHighlighter>
            </div>
          ) : (
            <code 
              className={className} 
              {...props}
              style={{
                backgroundColor: "#f3f4f6",
                padding: "2px 6px",
                borderRadius: "4px",
                fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                fontSize: "0.95em",
                color: "#e11d48",
                border: "1px solid #e5e7eb"
              }}
            >
              {children}
            </code>
          );
        },
        pre({ children }) {
          // Skip pre wrapper completely for code blocks (they handle their own containers)
          return <>{children}</>;
        },
        // Remove inline styles to allow claudeTheme.css to apply properly
        p: ({ children, ...props }) => (
          <p {...props}>
            {children}
          </p>
        ),
        ul: ({ children, ...props }) => (
          <ul {...props}>
            {children}
          </ul>
        ),
        ...components // Allow overriding components from props
      }}
    >
      {children}
    </Markdown>
  );
};

export default MarkdownRenderer;
